import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { createAuthenticatedClient } from '@/lib/graphql';
import { useAuth } from '@/lib/auth/AuthContext';
import type {
  Feature,
  FeatureFlagUser,
  FeatureConnection,
  FeatureFlagUserConnection,
  CreateFeatureInput,
  CreateFeaturePayload,
  UpdateFeatureInput,
  UpdateFeaturePayload,
  DeleteFeaturePayload,
  ToggleUserFeatureInput,
  ToggleUserFeaturePayload,
  SetUserFeaturesInput,
  SetUserFeaturesPayload,
} from '@/graphql/generated/graphql';

// Re-export types for convenience
export type { Feature, FeatureFlagUser };

// Queries
const GET_FEATURES = gql`
  query GetFeatures($first: Int, $after: String) {
    features(first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          featureKey
          name
          description
          enabledForAll
          enabledUserCount
          createdAt
          createdBy
          updatedAt
          updatedBy
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

const GET_FEATURE = gql`
  query GetFeature($featureKey: String!) {
    feature(featureKey: $featureKey) {
      id
      featureKey
      name
      description
      enabledForAll
      enabledUserCount
      createdAt
      createdBy
      updatedAt
      updatedBy
    }
  }
`;

const GET_FEATURE_FLAG_USERS = gql`
  query GetFeatureFlagUsers($first: Int, $after: String, $search: String) {
    featureFlagUsers(first: $first, after: $after, search: $search) {
      edges {
        cursor
        node {
          id
          userId
          email
          featureAssignments {
            featureKey
            enabled
          }
          enabledFeatures
          lastLoginAt
          createdAt
          updatedAt
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

const GET_FEATURE_FLAG_USER = gql`
  query GetFeatureFlagUser($email: String!) {
    featureFlagUser(email: $email) {
      id
      userId
      email
      featureAssignments {
        featureKey
        enabled
      }
      enabledFeatures
      lastLoginAt
      createdAt
      updatedAt
    }
  }
`;

// Mutations
const CREATE_FEATURE = gql`
  mutation CreateFeature($input: CreateFeatureInput!) {
    createFeature(input: $input) {
      feature {
        id
        featureKey
        name
        description
        enabledForAll
        enabledUserCount
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
      featureEdge {
        cursor
        node {
          id
          featureKey
        }
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

const UPDATE_FEATURE = gql`
  mutation UpdateFeature($input: UpdateFeatureInput!) {
    updateFeature(input: $input) {
      feature {
        id
        featureKey
        name
        description
        enabledForAll
        enabledUserCount
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

const DELETE_FEATURE = gql`
  mutation DeleteFeature($input: DeleteFeatureInput!) {
    deleteFeature(input: $input) {
      deletedFeatureId
      deletedFeatureKey
      userErrors {
        field
        message
        code
      }
    }
  }
`;

const TOGGLE_USER_FEATURE = gql`
  mutation ToggleUserFeature($input: ToggleUserFeatureInput!) {
    toggleUserFeature(input: $input) {
      feature {
        id
        featureKey
        name
        description
        enabledForAll
        enabledUserCount
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

const SET_USER_FEATURES = gql`
  mutation SetUserFeatures($input: SetUserFeaturesInput!) {
    setUserFeatures(input: $input) {
      features {
        id
        featureKey
        name
        description
        enabledForAll
        enabledUserCount
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

// Query Hooks
export function useFeatures(first = 100) {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: ['features', first],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ features: FeatureConnection }>(
        GET_FEATURES,
        { first }
      );
      return data.features;
    },
    enabled: !!accessToken,
  });
}

export function useFeature(featureKey: string | null) {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: ['feature', featureKey],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      if (!featureKey) throw new Error('Feature key is required');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ feature: Feature | null }>(
        GET_FEATURE,
        { featureKey }
      );
      return data.feature;
    },
    enabled: !!accessToken && !!featureKey,
  });
}

export function useFeatureFlagUsers(search?: string, first = 100) {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: ['featureFlagUsers', search, first],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ featureFlagUsers: FeatureFlagUserConnection }>(
        GET_FEATURE_FLAG_USERS,
        { first, search: search || undefined }
      );
      return data.featureFlagUsers;
    },
    enabled: !!accessToken,
  });
}

export function useFeatureFlagUser(email: string | null) {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: ['featureFlagUser', email],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      if (!email) throw new Error('Email is required');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ featureFlagUser: FeatureFlagUser | null }>(
        GET_FEATURE_FLAG_USER,
        { email }
      );
      return data.featureFlagUser;
    },
    enabled: !!accessToken && !!email,
  });
}

// Mutation Hooks
export function useCreateFeature() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateFeatureInput) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ createFeature: CreateFeaturePayload }>(
        CREATE_FEATURE,
        { input }
      );
      return data.createFeature;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
    },
  });
}

export function useUpdateFeature() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateFeatureInput) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ updateFeature: UpdateFeaturePayload }>(
        UPDATE_FEATURE,
        { input }
      );
      return data.updateFeature;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      queryClient.invalidateQueries({ queryKey: ['feature', variables.featureKey] });
    },
  });
}

export function useDeleteFeature() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (featureKey: string) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ deleteFeature: DeleteFeaturePayload }>(
        DELETE_FEATURE,
        { input: { featureKey } }
      );
      return data.deleteFeature;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      queryClient.invalidateQueries({ queryKey: ['featureFlagUsers'] });
    },
  });
}

export function useToggleUserFeature() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ToggleUserFeatureInput) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ toggleUserFeature: ToggleUserFeaturePayload }>(
        TOGGLE_USER_FEATURE,
        { input }
      );
      return data.toggleUserFeature;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      queryClient.invalidateQueries({ queryKey: ['feature', variables.featureKey] });
      queryClient.invalidateQueries({ queryKey: ['featureFlagUsers'] });
      // Invalidate individual user queries for all affected users
      for (const email of variables.userEmails) {
        queryClient.invalidateQueries({ queryKey: ['featureFlagUser', email] });
      }
    },
  });
}

export function useSetUserFeatures() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SetUserFeaturesInput) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ setUserFeatures: SetUserFeaturesPayload }>(
        SET_USER_FEATURES,
        { input }
      );
      return data.setUserFeatures;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      queryClient.invalidateQueries({ queryKey: ['featureFlagUsers'] });
      // Invalidate individual user queries for all affected users
      for (const email of variables.userEmails) {
        queryClient.invalidateQueries({ queryKey: ['featureFlagUser', email] });
      }
      // Invalidate individual feature queries for all affected features
      for (const feature of variables.features) {
        queryClient.invalidateQueries({ queryKey: ['feature', feature.featureKey] });
      }
    },
  });
}
